import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import React, { useEffect, useState } from 'react';
import {
  useUserState,
  useUserDispatch,
} from '@/components/context/UserContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import personalCodeValidation from '../../../components/hooks/personalCodeValidation';
import { MaterialIcon } from '@/components/ui/shared/materialIcon';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const basePath = '/userAccount';

const sidebarMenu = [
  {
    items: [
      {
        label: 'Asmeninė informacija',
        icon: 'person',
        link: `${basePath}/personal-Info`,
      },
      {
        label: 'Mano vizitai',
        icon: 'calendar_month',
        link: `${basePath}/visits`,
      },
      {
        label: 'Mano mokėjimai',
        icon: 'wallet',
        link: `${basePath}/payments`,
      },
      {
        label: 'Gydytojų konsultacijos',
        icon: 'stethoscope',
        link: `${basePath}/E025`,
      },
      {
        label: 'Receptai',
        icon: 'prescriptions',
        link: `${basePath}/prescriptions`,
      },
      {
        label: 'Siuntimai',
        icon: 'stethoscope_arrow',
        link: `${basePath}/referrals`,
      },
      {
        label: 'Instrumentiniai tyrimai',
        icon: 'radiology',
        link: `${basePath}/instrumental-researches`,
      },
      {
        label: 'Skiepai',
        icon: 'vaccines',
        link: `${basePath}/vaccinations`,
      },
      {
        label: 'Laboratoriniai tyrimai',
        icon: 'labs',
        link: `${basePath}/laboratory-researches`,
      },
      {
        label: 'Patologiniai tyrimai',
        icon: 'biotech',
        link: `${basePath}/pathology-researches`,
      },
      {
        label: 'Kitos paslaugos',
        icon: 'more_horiz',
        link: `${basePath}/other`,
      },
      {
        label: 'Kompleksinės paslaugos',
        icon: 'lab_profile',
        link: `${basePath}/complex-services`,
      },
      {
        label: 'Kuponai',
        icon: 'redeem',
        link: `${basePath}/coupons`,
      },
    ],
  },
  {
    section: 'Kitos paslaugos',
    items: [
      {
        label: 'Registracijos',
        icon: 'person_edit',
        link: `/`,
      },
      // {
      //   label: 'Registracijos į eilę',
      //   icon: 'pending_actions',
      //   link: `${basePath}/registration-queue`,
      // },
      {
        label: 'Susisiekti su budinčiu gydytoju',
        icon: 'local_hospital',
        link: `${basePath}/duty-doctor`,
      },
    ],
  },
];

const UserAccountSidebar = ({ steps }) => {
  const userState = useUserState();
  const userSessionDispatch = useUserDispatch();
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [children, setChildren] = useState([]);
  const [maxAgeFromPersonalCode] = personalCodeValidation();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    getChilds();
  }, [
    userState.contacts,
    userState.espbiEsi,
    userState.firstName,
    userState.lastName,
  ]);

  const getUsersName = () => {
    if (userState?.espbiEsi) {
      const child = userState.contacts.find(
        (contact) => contact.espbiEsi === userState.espbiEsi,
      );
      return `${child?.name} ${child?.surname}`;
    }
    return `${userState?.firstName} ${userState?.lastName}`;
  };

  const getChilds = () => {
    if (!userState?.contacts?.length) return;

    const isUnder18 = (personalCode) =>
      personalCode ? maxAgeFromPersonalCode(personalCode, 18) : true;

    const validRelationships = ['Dukra', 'Sunus', 'Isunis', 'Idukra'];

    let children = userState.contacts
      .filter(
        (contact) =>
          isUnder18(contact.personalCode) &&
          validRelationships.includes(contact.relationship),
      )
      .map((child) => ({
        name: child.name,
        surname: child.surname,
        espbiEsi: child.espbiEsi,
      }))
      .filter((child) => child.espbiEsi !== userState.espbiEsi);

    if (userState.espbiEsi) {
      children.unshift({
        name: userState.firstName,
        surname: userState.lastName,
        espbiEsi: null,
      });
    }

    setChildren(children);
  };

  const onChildSelect = (ch) => {
    userSessionDispatch({
      type: 'SET_CHILD',
      data: {
        espbiEsi: ch.espbiEsi,
      },
    });
  };

  const handleLogout = async () => {
    userSessionDispatch({ type: 'LOGOUT' });
  };

  const renderSidebarItems = () => (
    <>
      {sidebarMenu[0].items.map((item, i) => (
        <SidebarItem
          key={i}
          icon={item.icon}
          label={item.label}
          link={item.link}
        />
      ))}

      {sidebarMenu.slice(1).map((section, idx) => (
        <div key={idx} className="mt-2">
          {section.section && (
            <div className="min-h-10 flex items-center px-2 sm:px-4">
              <div className="text-xs font-semibold text-gray-500">
                {section.section}
              </div>
            </div>
          )}
          {section.items.map((item, i) => (
            <SidebarItem
              key={i}
              icon={item.icon}
              label={item.label}
              link={item.link}
            />
          ))}
        </div>
      ))}
    </>
  );

  return (
    <>
      <div className="block sm:hidden text-center">
        <Header
          fullName={getUsersName()}
          childs={children}
          onChildSelect={onChildSelect}
        />
      </div>

      <Card className="w-full my-4 sm:my-0">
        <CardHeader className="hidden sm:block">
          <div className="justify-self-start">
            <Header
              fullName={getUsersName()}
              childs={children}
              onChildSelect={onChildSelect}
            />
          </div>
        </CardHeader>

        {/* Mobile: show collapsible menu */}
        {isMobile ? (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between w-full h-12 cursor-pointer px-4">
                <span className="flex items-center space-x-2 text-base/6 font-semibold text-primary">
                  <MaterialIcon icon="menu" size={24} />
                  <span>Meniu</span>
                </span>
                {expanded ? (
                  <ChevronUp size={20} className="text-[#5180CA]" />
                ) : (
                  <ChevronDown size={20} className="text-[#5180CA]" />
                )}
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pb-4 pt-2 px-0 sm:px-0">
                {renderSidebarItems()}
              </CardContent>
              <CardFooter className="border-t-1" onClick={handleLogout}>
                <SidebarItem icon={'logout'} label={'Atsijungti'} link="/" />
              </CardFooter>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <>
            <CardContent className="sm:pb-4 px-0 sm:px-0">
              {renderSidebarItems()}
            </CardContent>
            <CardFooter
              className="px-0 sm:px-0 border-t-1"
              onClick={handleLogout}
            >
              <SidebarItem icon={'logout'} label={'Atsijungti'} link="/" />
            </CardFooter>
          </>
        )}
      </Card>
    </>
  );
};

const Header = ({ fullName, childs = [], onChildSelect }) => {
  const hasChildren = childs.length > 0;

  return (
    <>
      <div className="text-sm/6 font-normal text-affidea-gray">
        Mano paskyra
      </div>

      {hasChildren ? (
        <div className="flex w-full justify-center sm:justify-start mt-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 text-base/6 font-semibold text-primary outline-none">
                <span>{fullName}</span>
                <ChevronDown className="w-4 h-4 text-primary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {childs.map((child, index) => (
                <DropdownMenuItem
                  key={index}
                  onSelect={() => onChildSelect(child)}
                >
                  {child.name} {child.surname}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="text-base/6 font-semibold text-primary">{fullName}</div>
      )}
    </>
  );
};

const SidebarItem = ({ icon, label, link = '#' }) => {
  const location = useLocation();

  const normalize = (path) => path.replace(/\/+$/, '').toLowerCase();
  const currentPath = normalize(location.pathname);
  const targetPath = normalize(link);

  const isUnderUserAccount = link
    .toLocaleLowerCase()
    .startsWith(basePath.toLocaleLowerCase());
  const isActive =
    isUnderUserAccount &&
    (currentPath === targetPath || currentPath.startsWith(targetPath + '/'));

  return (
    <a
      href={link}
      className={`flex items-center space-x-2 w-full min-h-12 text-base/6 px-2 sm:px-4 ${
        isActive ? 'text-primary font-semibold bg-background px-2' : ''
      }`}
    >
      <MaterialIcon icon={icon} size={24} />
      <span>{label}</span>
    </a>
  );
};

export default UserAccountSidebar;
